import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    Paper,
    TableBody,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import EventBus from "../../EventBus";
import { EventBusEventsEnum } from "../../enums/EventBusEventsEnum";
import { CustomSnackbarTypes } from "../Common/CustomSnackBar";
import BookByDishService from "../../services/BookByDishService";
import {
    BlueTOpBorderContainer,
    CustomTableRowTR,
    InfoPill,
    SectionTitleRow,
    StyledDateText,
    StyledTable,
    TableData,
    TableHeader,
    TableTopHeader,
    TableWrapper,
    Title,
    WorkspaceContainer,
    WorkspaceHeader,
    WorkspaceIconBadge,
    WorkspacePanel,
    WorkspaceTitle,
} from "../../styles/commonStyles";
import { EditIcon } from "../Svg";
import DishPickerRow, { DishOption } from "./DishPickerRow";
import { BookByDishCountUnit } from "../../enums/BookByDishCountUnit";
import { dishCountUnit, isPlateLine, lineQuantityText, plateUnitLabel } from "../../utils/BookByDishPlate";

interface EditItem {
    dishId: number;
    dishName: string;
    count: number;
    price?: number;
    time?: number;
    dishType?: string;
    // The line's own unit snapshot. A line booked in pieces stays in pieces when its count is
    // edited, even if the dish has since gained a plate configuration — the admin is changing a
    // quantity, not converting the line.
    countUnit?: string;
    plateSize?: number | null;
    pieces?: number;
    plateUnit?: string;
}

// Every line carries its own unit: an unchanged plate line sent without PLATE would read as a
// unit switch and be re-priced as pieces.
const toEditPayload = (items: EditItem[]) =>
    items
        .filter((it) => it.count >= 1)
        .map((it) => ({
            dishId: it.dishId,
            count: Number(it.count),
            ...(isPlateLine(it) ? { countUnit: BookByDishCountUnit.PLATE } : {}),
        }));

const bookByDishService = new BookByDishService();

const showError = (message: string) => {
    EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, { message, type: CustomSnackbarTypes.ERROR });
};

const showSuccess = (message: string) => {
    EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, { message, type: CustomSnackbarTypes.SUCCESS });
};

const BookByDishEditForm = ({ partyId, party, onRefreshParty }: { partyId: number; party: any; onRefreshParty?: () => void }) => {
    const navigate = useNavigate();
    // Rendered under both /ChefForParty/:id and /BookByDish/:id — stay in the
    // module the user came from so the sidebar highlight is preserved.
    const resource = useLocation().pathname.split("/")[1] || "ChefForParty";

    const [items, setItems] = useState<EditItem[]>([]);
    const [dishes, setDishes] = useState<DishOption[]>([]);
    const [submitting, setSubmitting] = useState<boolean>(false);

    // Count picker dialog — for editing count of existing table-row items
    const [countPickerOpen, setCountPickerOpen] = useState(false);
    const [countPickerValue, setCountPickerValue] = useState<number>(1);
    const [countPickerTarget, setCountPickerTarget] = useState<'add' | number>('add');

    const openCountPicker = (target: 'add' | number, currentValue: number) => {
        setCountPickerTarget(target);
        setCountPickerValue(currentValue);
        setCountPickerOpen(true);
    };

    // dishType from the party payload when present, else from the dish catalogue.
    const getDishType = (dishId: number): string | undefined =>
        items.find((it) => it.dishId === dishId)?.dishType ??
        dishes.find((d) => d.id === dishId)?.dishType;

    // Unit noun for an existing line's count picker: the line's unit, not the dish's.
    const getItemCountUnit = (dishId: number): string | null => {
        const it = items.find((i) => i.dishId === dishId);
        if (it && isPlateLine(it)) return plateUnitLabel(it.plateUnit, 2);
        return dishCountUnit(getDishType(dishId));
    };

    useEffect(() => {
        const byDishId = new Map<number, EditItem>();
        (party?.dishes || [])
            .filter((d: any) => !d.deletedAt)
            .forEach((d: any) => {
                const dishId = d.dish?.id ?? d.dishId;
                if (dishId == null) return;
                byDishId.set(dishId, {
                    dishId,
                    dishName: d.dishName ?? d.dish?.name ?? `Dish #${dishId}`,
                    count: d.count ?? 1,
                    price: d.price ?? undefined,
                    time: d.time ?? undefined,
                    dishType: d.dish?.dishType ?? d.dishType ?? undefined,
                    countUnit: d.countUnit ?? undefined,
                    plateSize: d.plateSize ?? null,
                    pieces: d.pieces ?? undefined,
                    plateUnit: d.dish?.plateUnit ?? undefined,
                });
            });
        setItems(Array.from(byDishId.values()));
    }, [party]);

    useEffect(() => {
        bookByDishService
            .getBookByDishDishes()
            .then((list) => {
                const seen = new Set<number>();
                const unique = list
                    .map((d: any) => ({ id: d.id, name: d.name, dishType: d.dishType, plateSize: d.plateSize, plateUnit: d.plateUnit }))
                    .filter((d: DishOption) => {
                        if (seen.has(d.id)) return false;
                        seen.add(d.id);
                        return true;
                    });
                setDishes(unique);
            })
            .catch(() => showError("Failed to load dishes"));
    }, []);

    const updateCount = (dishId: number, count: number) => {
        const updated = items.map((it) =>
            it.dishId === dishId
                ? { ...it, count, pieces: isPlateLine(it) ? count * Number(it.plateSize) : count }
                : it,
        );
        setItems(updated);
        const payload = toEditPayload(updated);
        setSubmitting(true);
        bookByDishService
            .editVisit(partyId, { items: payload })
            .then(() => {
                showSuccess("Count updated");
                onRefreshParty?.();
            })
            .catch(() => showError("Failed to update count"))
            .finally(() => setSubmitting(false));
    };

    const removeItem = (dishId: number) => {
        const newItems = items.filter((it) => it.dishId !== dishId);
        setItems(newItems);
        const payload = toEditPayload(newItems);
        setSubmitting(true);
        bookByDishService
            .editVisit(partyId, { items: payload })
            .then(() => {
                showSuccess("Dish removed");
                onRefreshParty?.();
            })
            .catch(() => showError("Failed to remove dish"))
            .finally(() => setSubmitting(false));
    };

    const handleAddFromPicker = (dish: DishOption, count: number, price?: number, time?: number, countUnit?: BookByDishCountUnit) => {
        if (items.some((it) => it.dishId === dish.id)) {
            showError("Dish is already in the list — edit its count instead");
            return;
        }
        const plateSize = countUnit === BookByDishCountUnit.PLATE ? Number(dish.plateSize) : null;
        const newItems: EditItem[] = [...items, {
            dishId: dish.id,
            dishName: dish.name,
            count,
            price,
            time,
            dishType: dish.dishType,
            countUnit: countUnit ?? BookByDishCountUnit.PIECE,
            plateSize,
            pieces: count * (plateSize || 1),
            plateUnit: dish.plateUnit,
        }];
        setItems(newItems);
        const payload = toEditPayload(newItems);
        setSubmitting(true);
        bookByDishService
            .editVisit(partyId, { items: payload })
            .then(() => {
                showSuccess("Dish added");
                onRefreshParty?.();
            })
            .catch((err: any) => showError(err?.response?.data?.message || "Failed to add dish"))
            .finally(() => setSubmitting(false));
    };

    const handleSubmit = () => {
        const payloadItems = toEditPayload(items);
        if (payloadItems.length === 0) { showError("Visit must have at least one dish"); return; }
        setSubmitting(true);
        bookByDishService
            .editVisit(partyId, { items: payloadItems })
            .then((updated) => {
                showSuccess("Edited Successfully");
                navigate(`/${resource}/${updated?.id ?? partyId}/show`, { replace: true });
            })
            .catch(() => showError("Failed to edit visit"))
            .finally(() => setSubmitting(false));
    };

    return (
        <WorkspaceContainer>
            {/* Count picker dialog */}
            <Dialog open={countPickerOpen} onClose={() => setCountPickerOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    Select Count
                    {typeof countPickerTarget === 'number' && getItemCountUnit(countPickerTarget)
                        ? ` (${getItemCountUnit(countPickerTarget)})`
                        : ""}
                </DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        type="number"
                        label="Count"
                        value={countPickerValue === 0 ? '' : countPickerValue}
                        onChange={(e) => {
                            const raw = e.target.value;
                            if (raw === '') { setCountPickerValue(0); }
                            else { const val = Number(raw); if (val >= 0) setCountPickerValue(val); }
                        }}
                        InputProps={{ inputProps: { min: 1 } }}
                        size="small"
                        sx={{ mt: 1, mb: 2 }}
                    />
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, maxHeight: '60vh', overflowY: 'auto' }}>
                        {Array.from({ length: 100 }, (_, i) => i + 1).map((num) => (
                            <Button
                                key={num}
                                variant={countPickerValue === num ? 'contained' : 'outlined'}
                                size="small"
                                sx={{ minWidth: 44 }}
                                onClick={() => {
                                    if (countPickerTarget === 'add') {
                                        // no-op: add row is now handled inside DishPickerRow
                                    } else {
                                        updateCount(countPickerTarget as number, num);
                                    }
                                    setCountPickerValue(num);
                                    setCountPickerOpen(false);
                                }}
                            >
                                {num}
                            </Button>
                        ))}
                    </Box>
                    {/* Counts above 100 have no button — let the typed value be submitted. */}
                    {countPickerValue > 100 && (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                            <Button
                                variant="contained"
                                color="success"
                                sx={{ textTransform: 'none', boxShadow: 'none' }}
                                onClick={() => {
                                    if (countPickerTarget !== 'add') {
                                        updateCount(countPickerTarget as number, countPickerValue);
                                    }
                                    setCountPickerOpen(false);
                                }}
                            >
                                Submit {countPickerValue}
                            </Button>
                        </Box>
                    )}
                </DialogContent>
            </Dialog>

            {/* Header */}
            <WorkspaceHeader>
                <SectionTitleRow>
                    <WorkspaceIconBadge>
                        <EditIcon />
                    </WorkspaceIconBadge>
                    <WorkspaceTitle>Edit Book by Dish</WorkspaceTitle>
                </SectionTitleRow>
                <InfoPill>
                    <Typography variant="body2" fontWeight={500}>Visit #{partyId}</Typography>
                </InfoPill>
            </WorkspaceHeader>

            <WorkspacePanel>
                {/* Add dish row */}
                <DishPickerRow
                    dishes={dishes}
                    areaId={party?.areaId}
                    disabled={submitting}
                    onAdd={handleAddFromPicker}
                />

                {/* Dishes table + Summary side by side (mirrors create cart page) */}
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: items.length ? "2fr 1fr" : "1fr",
                        gap: "16px",
                        mt: 3,
                        alignItems: "start",
                    }}
                >
                    {/* Items table */}
                    <TableWrapper>
                        <TableTopHeader>
                            <Title>Dishes</Title>
                        </TableTopHeader>
                        <TableContainer component={Paper} sx={{ borderRadius: "8px" }}>
                            <StyledTable style={{ tableLayout: "fixed", width: "100%" }}>
                                <colgroup>
                                    <col style={{ width: "30%" }} />
                                    <col style={{ width: "20%" }} />
                                    <col style={{ width: "14%" }} />
                                    <col style={{ width: "14%" }} />
                                    <col style={{ width: "22%" }} />
                                </colgroup>
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: "rgba(232, 235, 237, 0.3) !important" }}>
                                        <TableHeader>Dish</TableHeader>
                                        <TableHeader style={{ textAlign: "center" }}>Count</TableHeader>
                                        <TableHeader style={{ textAlign: "center" }}>Price</TableHeader>
                                        <TableHeader style={{ textAlign: "center" }}>Time (min)</TableHeader>
                                        <TableHeader style={{ textAlign: "center" }}>Action</TableHeader>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {items.length ? (
                                        items.map((it) => (
                                            <CustomTableRowTR key={it.dishId}>
                                                <TableData>{it.dishName}</TableData>
                                                <TableData style={{ textAlign: "center" }}>
                                                    <StyledDateText
                                                        type="text"
                                                        value={lineQuantityText(it, { dishType: getDishType(it.dishId), plateUnit: it.plateUnit })}
                                                        InputProps={{ inputProps: { readOnly: true } }}
                                                        onClick={() => openCountPicker(it.dishId, it.count)}
                                                        variant="outlined"
                                                        label=""
                                                        sx={{ width: "100%", maxWidth: "180px", '& input': { padding: '8px 6px', fontSize: '14px', cursor: 'pointer', textAlign: 'center' }, '& .MuiOutlinedInput-root': { cursor: 'pointer' } }}
                                                    />
                                                </TableData>
                                                <TableData style={{ textAlign: "center" }}>
                                                    {it.price != null ? `₹${it.price}` : "-"}
                                                </TableData>
                                                <TableData style={{ textAlign: "center" }}>
                                                    {it.time != null ? it.time : "-"}
                                                </TableData>
                                                <TableData style={{ textAlign: "center" }}>
                                                    <Button
                                                        variant="contained"
                                                        color="error"
                                                        size="small"
                                                        startIcon={<DeleteOutlineIcon />}
                                                        onClick={() => removeItem(it.dishId)}
                                                        sx={{ textTransform: "none", boxShadow: "none", fontSize: "12px" }}
                                                    >
                                                        Remove
                                                    </Button>
                                                </TableData>
                                            </CustomTableRowTR>
                                        ))
                                    ) : (
                                        <CustomTableRowTR>
                                            <TableData colSpan={5} style={{ textAlign: "center", padding: "24px" }}>
                                                No dishes — add at least one.
                                            </TableData>
                                        </CustomTableRowTR>
                                    )}
                                </TableBody>
                            </StyledTable>
                        </TableContainer>
                    </TableWrapper>

                    {/* Summary — values from party API response */}
                    {items.length > 0 && (() => {
                        const visitCharges = Number(party?.visitCharges ?? 0);
                        const grandTotal   = Number(party?.totalAmount ?? 0);
                        const dishesTotal  = grandTotal - visitCharges;
                        const totalTime    = Number(party?.cookingTime ?? 0);
                        return (
                            <TableWrapper>
                                <TableTopHeader>
                                    <Title>Summary</Title>
                                </TableTopHeader>
                                <TableContainer component={Paper} sx={{ borderRadius: "8px" }}>
                                    <StyledTable style={{ tableLayout: "fixed", width: "100%" }}>
                                        <colgroup>
                                            <col style={{ width: "65%" }} />
                                            <col style={{ width: "35%" }} />
                                        </colgroup>
                                        <TableBody>
                                            <CustomTableRowTR>
                                                <TableData>Dishes</TableData>
                                                <TableData style={{ textAlign: "right" }}>₹{dishesTotal}</TableData>
                                            </CustomTableRowTR>
                                            <CustomTableRowTR>
                                                <TableData>Visit Charges</TableData>
                                                <TableData style={{ textAlign: "right" }}>₹{visitCharges}</TableData>
                                            </CustomTableRowTR>
                                            <CustomTableRowTR>
                                                <TableData>Surge Price</TableData>
                                                <TableData style={{ textAlign: "right" }}>₹{Number(party?.surgePrice ?? 0)}</TableData>
                                            </CustomTableRowTR>
                                            <CustomTableRowTR>
                                                <TableData style={{ fontWeight: 700 }}>Total</TableData>
                                                <TableData style={{ textAlign: "right", fontWeight: 700 }}>₹{grandTotal}</TableData>
                                            </CustomTableRowTR>
                                            <CustomTableRowTR>
                                                <TableData>Total Time</TableData>
                                                <TableData style={{ textAlign: "right" }}>{totalTime} min</TableData>
                                            </CustomTableRowTR>
                                        </TableBody>
                                    </StyledTable>
                                </TableContainer>
                            </TableWrapper>
                        );
                    })()}
                </Box>

                {/* Submit */}
                <BlueTOpBorderContainer>
                    <Typography variant="body2" color="text.secondary">
                        {items.length === 0 ? "Add at least one dish to continue." : `${items.length} dish(es) in visit`}
                    </Typography>
                    <Button
                        variant="contained"
                        color="success"
                        size="large"
                        onClick={handleSubmit}
                        disabled={submitting || items.length === 0}
                        sx={{ textTransform: "none", boxShadow: "none" }}
                    >
                        {submitting ? <><CircularProgress size={14} sx={{ mr: 1, color: '#fff' }} />Saving…</> : "Submit"}
                    </Button>
                </BlueTOpBorderContainer>
            </WorkspacePanel>
        </WorkspaceContainer>
    );
};

export default BookByDishEditForm;
